import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, inject, signal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TwoFactorVerificationModalComponent } from '@shared/components/two-factor-verification-modal/two-factor-verification-modal.component';
import { roles } from '@shared/constants/roles';
import type { Replace } from '@shared/helpers';
import { TwoFactorAuthenticationMethod } from '@shared/models';
import type {
  ITwoFactorVerificationService,
  VerificationCodeRequestResponse,
  VerificationFlowResult,
  VerifyCodeResponse,
} from '@shared/models/two-factor-verification';
import { IdentityServerDataService } from '@shared/services/identityserver-data.service';
import { JwtHelperService } from '@shared/services/jwt-decode';
import { Observable, firstValueFrom, map, tap } from 'rxjs';
import { AuthorizeService } from './authorize.service';

const verificationTokenStorageKey = 'emailVerificationToken';

@Injectable({
  providedIn: 'root',
})
export class TwoFactorVerificationService implements ITwoFactorVerificationService {
  private readonly authService = inject(AuthorizeService);
  private readonly modalService = inject(NgbModal);
  private readonly jwtService = inject(JwtHelperService);
  private readonly injector = inject(Injector);

  public readonly lastRequestVerificationResponses = signal(
    new Map<TwoFactorAuthenticationMethod, VerificationCodeRequestResponse | undefined>(),
  );
  public readonly lastVerificationAttemptResponses = signal(
    new Map<TwoFactorAuthenticationMethod, VerifyCodeResponse | undefined>(),
  );

  public get verificationToken(): string | null {
    const token = sessionStorage.getItem(verificationTokenStorageKey) ?? localStorage.getItem(verificationTokenStorageKey);

    // If token is not valid for the currently logged in user, do not return it
    return token && this.isTokenValid(token) ? token : null;
  }

  private get dataService(): IdentityServerDataService {
    // This has to be injected because the base abstract DataService (that IdentityServerDataService extends) needs to
    // inject this service. Therefore, if we tried to inject the ISDS in this service, it would cause a circular ref.
    return this.injector.get(IdentityServerDataService);
  }

  public verificationCodeExpiresAt(method: TwoFactorAuthenticationMethod): Date | null {
    return this.lastRequestVerificationResponses().get(method)?.expiresAt ?? null;
  }

  public canRequestVerificationCodeAt(method: TwoFactorAuthenticationMethod): Date | null {
    return this.lastRequestVerificationResponses().get(method)?.requestAgainAt ?? null;
  }

  public canAttemptVerificationAt(method: TwoFactorAuthenticationMethod): Date | null {
    const res = this.lastVerificationAttemptResponses().get(method);
    return res?.succeeded === false && res.tryAgainAt ? new Date(res.tryAgainAt) : null;
  }

  public requiresNewVerificationCode(method: TwoFactorAuthenticationMethod): boolean {
    const res = this.lastVerificationAttemptResponses().get(method);
    return res?.succeeded === false && res.requiresNewCode;
  }

  public async assertVerified(): Promise<void> {
    // If the current user is an SSO user AND there is no verification token setup, begin the flow.
    // Non-SSO users are always verified as their email address must be confirmed before they're able to log in
    const isSsoUser = await firstValueFrom(this.authService.isSsoUser$);
    if (isSsoUser && !this.authService.hasRole(roles.ssoTesting) && !this.verificationToken) {
      await this.beginVerificationFlow();
    }
  }

  public async beginVerificationFlow(): Promise<void> {
    const result = (await this.modalService.open(TwoFactorVerificationModalComponent, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    }).result) as VerificationFlowResult;
    if (result.shouldSave) {
      this.persistVerificationToken();
    }
  }

  public requestVerificationCode(method?: TwoFactorAuthenticationMethod): Observable<VerificationCodeRequestResponse> {
    const query = method ? `?method=${method}` : '';
    return this.dataService
      .post<Replace<VerificationCodeRequestResponse, Date, string>>(
        `TwoFactorAuthentication/BeginVerify${query}`, {}, null, { showErrorToast: false, logErrorToConsole: false },
      ).pipe(
        map(res => ({
          ...res,
          expiresAt: res.expiresAt ? new Date(res.expiresAt) : null,
          requestAgainAt: res.requestAgainAt ? new Date(res.requestAgainAt) : null,
        })),
        tap(res => {
          this.lastRequestVerificationResponses.update(x => new Map(x).set(res.method, res));
          this.lastVerificationAttemptResponses.update(x => new Map(x).set(res.method, undefined));
        }),
      );
  }

  public submitVerificationCode(code: string, method: TwoFactorAuthenticationMethod): Observable<VerifyCodeResponse> {
    return this.dataService.post<VerifyCodeResponse>('TwoFactorAuthentication/CompleteVerify', { code, method }, null, { showErrorToast: false }).pipe(
      tap({
        next: res => {
          this.lastVerificationAttemptResponses.update(x => new Map(x).set(method, res));
          if (res.succeeded) {
            sessionStorage.setItem(verificationTokenStorageKey, res.token);
            localStorage.removeItem(verificationTokenStorageKey);
          }
        },
        error: err => {
          if (err instanceof HttpErrorResponse && err.error?.result) {
            this.lastVerificationAttemptResponses.update(x => new Map(x).set(method, err.error?.result as VerifyCodeResponse));
          }
        },
      }),
    );
  }

  public persistVerificationToken(): void {
    if (this.verificationToken) {
      localStorage.setItem(verificationTokenStorageKey, this.verificationToken);
    }
  }

  public setVerificationToken(token: string, rememberMe: boolean = true): void {
    this.unsetVerificationToken();
    sessionStorage.setItem(verificationTokenStorageKey, token);
    if (rememberMe) {
      this.persistVerificationToken();
    }
  }

  public unsetVerificationToken(): void {
    sessionStorage.removeItem(verificationTokenStorageKey);
    localStorage.removeItem(verificationTokenStorageKey);
  }

  /** Determines if the given token is valid for the currently authenticated user at the current time. */
  public isTokenValid(token: string): boolean {
    try {
      const tokenObj = this.jwtService.decodeToken(token);
      return !this.jwtService.isTokenExpired(token) && !!this.authService.user && tokenObj.sub === this.authService.user.sub;
    } catch {
      return false;
    }
  }
}
